import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppRangePicker from "../../components/Core/AppRangePicker/AppRangePicker";
import { DownloadButton } from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { ConsultantWorkedHoursReportBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { ViewModels } from "../../configs/constants/ViewModels";
import {
	buildContractKeyValues,
	buildCustomerKeyValues,
	buildPoolKeyValues,
} from "../../configs/Reports.config";
import { KeyValue } from "../../models/KeyValue";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useContracts } from "../../networking/contract.hooks";
import { useCustomers } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { apiStore } from "../../stores/api.store";
import { reportStore } from "../../networking/report.api";

const ConsultantsWorkedHoursReportPage: React.FC = () => {
	const { t } = useTranslation();

	const { data: organisation } = useOrganisation();

	const [pool, setPool] = useState<KeyValue>();
	const [range, setRange] = useState<{ start: Date; end: Date }>();

	const [contract, setContract] = useState<KeyValue>();
	const [customer, setCustomer] = useState<KeyValue>();
	const [loading, setLoading] = useState<boolean>();
	const [hasErrorInDateRange, setHasErrorInDateRange] = useState<boolean>(false);

	const { contracts = [] } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		sort: CONTRACT_SORTING_COLUMN.NAME,
	});
	// Slim projection for the dropdown. The old store call used the default page
	// size (20), silently truncating the options — fetch them all instead.
	const { customers: customersData } = useCustomers({
		page: 1,
		pageSize: 10000,
		view: ViewModels.SELECT_OPTION,
		active: true,
	});

	const pools = buildPoolKeyValues(organisation?.pools || [], t("ALL"));
	const contractOptions = buildContractKeyValues(contracts || [], t("ALL"));
	const customerOptions = buildCustomerKeyValues(customersData?.customers || [], t("ALL"));

	const handlePoolChange = (e: KeyValue) => {
		setPool(e);
	};

	const handleContractChange = (e: KeyValue) => {
		setContract(e);
	};

	const handleCustomerChange = (e: KeyValue) => {
		setCustomer(e);
	};

	const onSubmit = async () => {
		let selectedStartDate!: string;
		let selectedEndDate!: string;
		let selectedPool!: string;
		let selectedContract!: string;
		let selectedCustomer!: string;

		if (pool?.key && pool.key !== "") selectedPool = pool.key as string;

		if (range && range.start.toISOString() !== "") selectedStartDate = range.start.toISOString();

		if (range && range.end.toISOString() !== "") selectedEndDate = range.end.toISOString();

		if (contract?.key && contract.key !== "") selectedContract = contract.key as string;

		if (customer?.key && customer.key !== "") selectedCustomer = customer.key as string;

		if (!selectedStartDate || !selectedEndDate) {
			setHasErrorInDateRange(true);
			return;
		}

		setLoading(true);
		const url = await reportStore.getConsultantsWorkedReport(
			selectedStartDate,
			selectedEndDate,
			selectedPool,
			selectedContract,
			selectedCustomer
		);

		setLoading(false);

		if (url) {
			// https://stackoverflow.com/a/59352848
			const downloadLink = document.createElement("a");
			downloadLink.href = apiStore.apiRoot + url;
			downloadLink.download = apiStore.apiRoot + url;
			downloadLink.click();
		}
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={ConsultantWorkedHoursReportBreadcrumbs} />
			<Flex margin="1.5rem 0" maxWidth="900px" flexWrap="wrap">
				<AppRangePicker
					label={t("reports.filters.range.placeHolder") + "*"}
					placeholder={t("reports.filters.range.label")}
					value={range}
					onChange={(range) => {
						setRange(range);
						setHasErrorInDateRange(false);
					}}
					containerMargin="0.5rem 0.5rem"
					containerWidth="300px"
					error={hasErrorInDateRange ? t("END_DATE_START_DATE_REQUIRED_VALIDATION") : ""}
				/>

				{pools.length > 0 && (
					<Dropdown<KeyValue, KeyValue>
						value={pool}
						getValue={(o) => o}
						onChange={handlePoolChange}
						getOptionLabel={(option) => option.value}
						options={pools}
						placeholder={"reports.filters.pool.placeHolder"}
						label={"reports.filters.pool.label"}
						width="300px"
						margin="0.5rem 0.5rem"
					/>
				)}
				<Dropdown<KeyValue, KeyValue>
					value={contract}
					getValue={(o) => o}
					onChange={handleContractChange}
					getOptionLabel={(option) => option.value}
					options={contractOptions}
					placeholder={"reports.filters.contract.placeHolder"}
					label={"reports.filters.contract.label"}
					width="300px"
					margin="0.5rem 0.5rem"
				/>
				<Dropdown<KeyValue, KeyValue>
					value={customer}
					getValue={(o) => o}
					onChange={handleCustomerChange}
					getOptionLabel={(option) => option.value}
					options={customerOptions}
					placeholder={"reports.filters.customer.placeHolder"}
					label={"reports.filters.customer.label"}
					width="300px"
					margin="0.5rem 0.5rem"
				/>
			</Flex>
			<Flex>
				<DownloadButton
					width="200px"
					height="38px"
					fontSize="m"
					margin="0.5rem"
					onClick={onSubmit}
					loading={loading}
				>
					{t("reports.export")}
				</DownloadButton>
			</Flex>
		</div>
	);
};

export default ConsultantsWorkedHoursReportPage;
